import React, { useState } from 'react';
import styled from 'styled-components';
import { BodyText } from '../Typography';
import { Icon, IconType } from '../Icon';
import { Button } from '../Button';
import { IHasTestData } from '../../test-utilities/IHasTestData';

export interface IErrorMessageProps extends IHasTestData {
  /** Provide a JavaScript Error object. This will auto render the error message. */
  error?: Error | null;
  /** Provide an error message. This will auto-style to BodyText. */
  message?: React.ReactNode | null;
  /** Provide custom error content. */
  children?: React.ReactNode | null;
}

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.colors.errorText};
  background-color: ${(props) => props.theme.colors.errorBackground};
  border: 1px solid ${(props) => props.theme.colors.errorBorder};
  border-radius: ${(props) => props.theme.spacing.spacing2};
  padding: ${(props) => props.theme.spacing.spacing4};
  text-align: center;
`;

const Error = styled.div<{ hasMessageOrChildren: boolean }>`
  ${(props) => (props.hasMessageOrChildren ? props.theme.fonts.mixins.tiny : props.theme.fonts.mixins.body)};
`;

const StackTraceInfoButton = styled(Button)`
  ${(props) => props.theme.fonts.tinyLineHeight};
  ${(props) => props.theme.fonts.tinySize};
  min-height: auto;
`;

const StackTraceInfoIcon = styled(Icon)`
  color: ${(props) => props.theme.colors.error};
  height: 12px; // from props.theme.fonts.tinySize but that variable is not usable since it is the entire rule declaration instead of just a size
  width: 12px; // from props.theme.fonts.tinySize but that variable is not usable since it is the entire rule declaration instead of just a size
`;

const StackTrace = styled.code`
  display: block;
  text-align: left;
`;

/**
 * Alert banner informing about an error in some part of the application.
 */
export const ErrorMessage: React.FC<IErrorMessageProps> = ({ children, error, message, ...rest }) => {
  const [showStackTrace, setShowStackTrace] = useState(false);
  return (
    ((!!children || !!error || !!message) && (
      <StyledErrorMessage {...rest} data-test-class={rest['data-test-class'] || 'error-message'}>
        {message && <BodyText>{message}</BodyText>}
        {children}
        {error && (
          <Error hasMessageOrChildren={!!error || !!children}>
            <div>
              <StackTraceInfoButton intent={'danger'} type="button" onClick={() => setShowStackTrace(!showStackTrace)}>
                <StackTraceInfoIcon type={IconType.InfoFill} />
              </StackTraceInfoButton>{' '}
              {error.message}
            </div>
            {showStackTrace && <StackTrace>{error.stack}</StackTrace>}
          </Error>
        )}
      </StyledErrorMessage>
    )) ||
    null
  );
};
